import React, { useEffect, useMemo, useState } from "react";
import { Hand, Armchair, Footprints, Timer, Pencil } from "lucide-react";
import { fetchAbleCareMeasurements } from "../../../utils/api";
import {
  KG_TO_LBS,
  ageFromDob,
  normaliseSex,
  interpretCst,
  interpretTug,
  interpretGait,
  interpretGrip,
} from "./ableCareThresholds";
import { buildAbleCareSample, SAMPLE_INDIVIDUAL } from "./ableCareMockData";
import "./AbleCareSection.css";

// Able Assess falls-risk screening: grip strength, 30-second chair stand,
// 4-metre gait speed and Timed Up and Go, all captured on one GripAble sensor
// and pushed to us by Able's webhook (screening.recorded / measurement.recorded).
// Each card can also be filled in by hand via "Manual entry".

const TESTS = [
  {
    type: "SMGT",
    field: "gripStrengthLbs",
    name: "Grip Strength",
    what: "Overall strength & frailty",
    Icon: Hand,
    accent: "blue",
    unit: "lbs",
    digits: 1,
    step: "0.1",
    higherIsBetter: true,
    // Entered per hand; `field` holds the stronger hand, as Able reports grip.
    hands: [
      { hand: "LEFT", field: "gripLeftLbs", label: "Left hand" },
      { hand: "RIGHT", field: "gripRightLbs", label: "Right hand" },
    ],
  },
  {
    type: "CST",
    field: "chairStand",
    name: "Chair Stand",
    what: "Lower-limb strength · stands in 30 s",
    Icon: Armchair,
    accent: "green",
    unit: "reps",
    digits: 0,
    step: "1",
    higherIsBetter: true,
  },
  {
    type: "GST",
    field: "gaitSpeedMs",
    name: "Gait Speed",
    what: "Mobility & functional reserve · 4 m walk",
    Icon: Footprints,
    accent: "violet",
    unit: "m/s",
    digits: 2,
    step: "0.01",
    higherIsBetter: true,
  },
  {
    type: "TUG",
    field: "timedUpAndGo",
    name: "Timed Up and Go",
    what: "Dynamic balance & mobility",
    Icon: Timer,
    accent: "orange",
    unit: "s",
    digits: 1,
    step: "0.1",
    higherIsBetter: false,
  },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const num = (v) => {
  const n = Number(v);
  return v == null || v === "" || !Number.isFinite(n) ? null : n;
};

// Grip arrives in kg per the webhook spec; the app reports grip in lbs.
const toLbs = (value, unit) => {
  const n = num(value);
  if (n == null) return null;
  return String(unit || "").toLowerCase() === "kg" ? n * KG_TO_LBS : n;
};

const displayValue = (m) => (m?.type === "SMGT" ? toLbs(m.result, m.unit) : num(m?.result));

const fmt = (n, digits) => (n == null ? "—" : n.toFixed(digits));

// Group flat measurements into screening sessions. Standalone measurements
// (measurement.recorded outside a screening) each become their own row.
function groupScreenings(measurements, screenings) {
  const meta = new Map((screenings || []).map((s) => [s.ableScreeningId, s]));
  const groups = new Map();
  for (const m of measurements) {
    const key = m.ableScreeningId || m.ableMeasurementId;
    if (!groups.has(key)) {
      const s = meta.get(m.ableScreeningId);
      groups.set(key, {
        id: key,
        isScreening: Boolean(m.ableScreeningId),
        at: s?.screenedAt || m.measuredAt,
        sensorId: s?.sensorId || m.sensorId,
        atRisk: s ? s.atRisk : undefined,
        byType: {},
      });
    }
    const g = groups.get(key);
    const t = String(m.type || "").toUpperCase();
    if (!g.byType[t]) g.byType[t] = m;
  }
  const rows = [...groups.values()].map((g) => {
    if (g.atRisk === undefined || g.atRisk === null) {
      const flags = Object.values(g.byType).map((m) => m.atRisk);
      g.atRisk = flags.includes(true) ? true : flags.includes(false) ? false : null;
    }
    return g;
  });
  rows.sort((a, b) => new Date(b.at) - new Date(a.at));
  return rows;
}

// Able's own at_risk flag is authoritative. The local STEADI/WGFP banding only
// supplies the finer label (Average vs Above average) when it agrees with Able.
const resolveStatus = (m, interp) => {
  const local = interp?.status ? { status: interp.status, label: interp.label } : null;
  if (m?.atRisk === true) {
    return local?.status === "risk" ? local : { status: "risk", label: "At risk" };
  }
  if (m?.atRisk === false) {
    return local && local.status !== "risk" ? local : { status: "good", label: "Not at risk" };
  }
  return local || { status: null, label: interp?.label || null };
};
const statusOf = (m, interp) => resolveStatus(m, interp).status;

function ThresholdBar({ scale, value }) {
  if (!scale) return null;
  const span = scale.max - scale.min;
  const pct = (v) => Math.min(100, Math.max(0, ((v - scale.min) / span) * 100));
  let from = scale.min;
  return (
    <div className="ablecare-bar" aria-hidden="true">
      <div className="ablecare-bar-track">
        {scale.segments.map((seg, i) => {
          const to = Math.min(seg.to, scale.max);
          const el = (
            <span
              key={i}
              className={`ablecare-bar-seg tone-${seg.tone}`}
              style={{ left: `${pct(from)}%`, width: `${pct(to) - pct(from)}%` }}
            />
          );
          from = to;
          return el;
        })}
        {value != null && <span className="ablecare-bar-marker" style={{ left: `${pct(value)}%` }} />}
      </div>
      <div className="ablecare-bar-ticks">
        {scale.ticks.map((t) => (
          <span key={t} style={{ left: `${pct(t)}%` }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status, label }) {
  if (!label) return null;
  return <span className={`ablecare-pill tone-${status || "none"}`}>{label}</span>;
}

// A card shows the manual value when one is saved (tagged "Manual"), otherwise
// the latest Able Care reading.
function MetricCard({ test, latest, previous, manualValues, interpretFor, editing, draft, onDraft }) {
  const manualValue = num(manualValues[test.field]);
  const useManual = manualValue != null;
  const manualHands = (test.hands || [])
    .map((h) => ({ hand: h.hand, result: num(manualValues[h.field]), unit: test.unit }))
    .filter((h) => h.result != null);
  const shown = useManual
    ? { type: test.type, result: manualValue, unit: test.unit, atRisk: null, values: manualHands }
    : latest;
  const value = displayValue(shown);
  const interp = interpretFor(test.type, value);
  const { status, label } = resolveStatus(shown, interp);

  const prevValue = displayValue(previous);
  const delta = !useManual && value != null && prevValue != null ? value - prevValue : null;
  const improved = delta == null || delta === 0 ? null : (delta > 0) === test.higherIsBetter;
  const hands = test.hands ? shown?.values || [] : [];
  const ableValue = displayValue(latest);
  const ableHand = (hand) => (latest?.values || []).find((v) => v.hand === hand);

  return (
    <div className={`ablecare-card accent-${test.accent} ${status === "risk" && !editing ? "is-risk" : ""}`}>
      <div className="ablecare-card-head">
        <span className="ablecare-card-icon"><test.Icon size={18} /></span>
        <div>
          <div className="ablecare-card-name">{test.name}</div>
          <div className="ablecare-card-what">{test.what}</div>
        </div>
      </div>

      {editing && test.hands ? (
        <div className="ablecare-hand-inputs">
          {test.hands.map((h) => {
            const able = ableHand(h.hand);
            return (
              <label key={h.field} className="ablecare-hand-input">
                <span>{h.label}</span>
                <span className="ablecare-input-wrap">
                  <input
                    type="number"
                    min="0"
                    step={test.step}
                    inputMode="decimal"
                    placeholder={able ? fmt(toLbs(able.result, able.unit), test.digits) : "—"}
                    value={draft[h.field] ?? ""}
                    onChange={(e) => onDraft(h.field, e.target.value)}
                    className="ablecare-input"
                    aria-label={`${test.name} ${h.label} (${test.unit})`}
                  />
                  <span className="ablecare-unit">{test.unit}</span>
                </span>
              </label>
            );
          })}
        </div>
      ) : editing ? (
        <label className="ablecare-input-wrap">
          <input
            type="number"
            min="0"
            step={test.step}
            inputMode="decimal"
            placeholder={ableValue != null ? fmt(ableValue, test.digits) : "—"}
            value={draft[test.field] ?? ""}
            onChange={(e) => onDraft(test.field, e.target.value)}
            className="ablecare-input"
            aria-label={`${test.name} (${test.unit})`}
          />
          <span className="ablecare-unit">{test.unit}</span>
        </label>
      ) : (
        <div className="ablecare-card-value">
          <span className="ablecare-num">{fmt(value, test.digits)}</span>
          <span className="ablecare-unit">{test.unit}</span>
          <StatusPill status={status} label={shown ? label : null} />
        </div>
      )}

      {editing && ableValue != null && (
        <div className="ablecare-sub">
          Able Care: {fmt(ableValue, test.digits)} {test.unit} on {fmtDate(latest.measuredAt)}
        </div>
      )}

      {!editing && useManual && <div className="ablecare-sub"><span className="ablecare-manual-tag">Manual</span></div>}

      {!editing && !useManual && test.type === "SMGT" && latest && String(latest.unit).toLowerCase() === "kg" && (
        <div className="ablecare-sub">{fmt(num(latest.result), 1)} kg (best hand)</div>
      )}

      {!editing && hands.length > 0 && (
        <div className="ablecare-hands">
          {hands.map((h, i) => (
            <div key={`${h.hand}-${i}`} className="ablecare-hand">
              <span>{h.hand ? `${h.hand[0]}${h.hand.slice(1).toLowerCase()} hand` : "Trial"}</span>
              <strong>{fmt(toLbs(h.result, h.unit), 1)} lbs</strong>
            </div>
          ))}
        </div>
      )}

      {!editing && shown && <ThresholdBar scale={interp?.scale} value={value} />}

      {!editing && (
        <div className="ablecare-card-foot">
          {interp?.note && <div className="ablecare-note">{interp.note}</div>}
          {delta != null && delta !== 0 && (
            <div className={`ablecare-delta ${improved ? "up" : "down"}`}>
              {delta > 0 ? "▲" : "▼"} {fmt(Math.abs(delta), test.digits)} {test.unit} since{" "}
              {fmtDate(previous.measuredAt)}
            </div>
          )}
          {!shown && <div className="ablecare-note">No reading yet</div>}
        </div>
      )}
    </div>
  );
}

export default function AbleCareSection({
  clientId,
  dob,
  gender,
  showSampleData = false,
  manualValues = {},
  canEdit = false,
  onSaveManual,
}) {
  const [state, setState] = useState({ loading: true, measurements: [], screenings: [] });
  const [view, setView] = useState("latest");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setState({ loading: false, measurements: [], screenings: [] });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    fetchAbleCareMeasurements(clientId)
      .then((res) => {
        if (cancelled) return;
        setState({
          loading: false,
          measurements: res?.measurements || [],
          screenings: res?.screenings || [],
        });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, measurements: [], screenings: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Real readings always win; the sample only fills an empty panel.
  const isSample = showSampleData && !state.loading && state.measurements.length === 0;
  const source = useMemo(() => (isSample ? buildAbleCareSample() : state), [isSample, state]);

  let age = ageFromDob(dob);
  let sex = normaliseSex(gender);
  if (isSample) {
    age = age ?? SAMPLE_INDIVIDUAL.age;
    sex = sex ?? SAMPLE_INDIVIDUAL.sex;
  }

  const rows = useMemo(
    () => groupScreenings(source.measurements || [], source.screenings),
    [source],
  );

  // Latest and previous reading per test, across screenings and standalone tests.
  const history = useMemo(() => {
    const out = {};
    const sorted = [...(source.measurements || [])].sort(
      (a, b) => new Date(b.measuredAt) - new Date(a.measuredAt),
    );
    for (const m of sorted) {
      const t = String(m.type || "").toUpperCase();
      (out[t] = out[t] || []).push(m);
    }
    return out;
  }, [source]);

  const interpretFor = (type, v) => {
    if (type === "CST") return interpretCst(v, age, sex);
    if (type === "TUG") return interpretTug(v, age);
    if (type === "GST") return interpretGait(v, age);
    return interpretGrip();
  };

  const editableFields = TESTS.flatMap((t) =>
    t.hands ? t.hands.map((h) => h.field) : [t.field],
  );

  const startEditing = () => {
    setDraft(Object.fromEntries(editableFields.map((f) => [f, manualValues[f] ?? ""])));
    setEditing(true);
  };

  const save = async () => {
    const values = Object.fromEntries(editableFields.map((f) => [f, num(draft[f]) ?? ""]));
    for (const t of TESTS) {
      if (!t.hands) continue;
      const handValues = t.hands.map((h) => values[h.field]).filter((v) => v !== "");
      values[t.field] = handValues.length ? Math.max(...handValues) : "";
    }
    setSaving(true);
    try {
      const ok = onSaveManual ? await onSaveManual(values) : true;
      if (ok !== false) setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ablecare-section">
      <div className="ablecare-toolbar">
        <div className="ablecare-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={view === "latest"}
            className={view === "latest" ? "active" : ""}
            onClick={() => setView("latest")}
          >
            Latest
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "history"}
            className={view === "history" ? "active" : ""}
            onClick={() => {
              setView("history");
              setEditing(false);
            }}
          >
            Screening history ({rows.length})
          </button>
        </div>

        <div className="ablecare-actions">
          {isSample && <span className="ablecare-sample">Sample data</span>}
          {view === "latest" && canEdit && !editing && (
            <button type="button" className="ablecare-btn" onClick={startEditing}>
              <Pencil size={14} /> Manual entry
            </button>
          )}
          {view === "latest" && editing && (
            <>
              <button
                type="button"
                className="ablecare-btn"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ablecare-btn primary"
                onClick={save}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {state.loading ? (
        <div className="ablecare-empty">Loading Able Care screenings…</div>
      ) : view === "latest" ? (
        <div className="ablecare-grid">
          {TESTS.map((t) => (
            <MetricCard
              key={t.type}
              test={t}
              latest={history[t.type]?.[0]}
              previous={history[t.type]?.[1]}
              manualValues={manualValues}
              interpretFor={interpretFor}
              editing={editing}
              draft={draft}
              onDraft={(field, v) => setDraft((d) => ({ ...d, [field]: v }))}
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="ablecare-empty">
          No Able Care screenings yet. They appear here once this client completes an Able
          Assess screening on a linked sensor.
        </div>
      ) : (
        <div className="ablecare-history">
          <div className="ablecare-table-wrap">
            <table className="ablecare-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {TESTS.map((t) => (
                    <th key={t.type}>{t.name} <span>({t.unit})</span></th>
                  ))}
                  <th>Falls risk</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {fmtDate(r.at)}
                      {!r.isScreening && <span className="ablecare-standalone">single test</span>}
                    </td>
                    {TESTS.map((t) => {
                      const m = r.byType[t.type];
                      const v = displayValue(m);
                      const st = m ? statusOf(m, interpretFor(t.type, v)) : null;
                      return (
                        <td key={t.type} className={st === "risk" ? "cell-risk" : ""}>
                          {m ? fmt(v, t.digits) : "—"}
                        </td>
                      );
                    })}
                    <td>
                      <StatusPill
                        status={r.atRisk === true ? "risk" : r.atRisk === false ? "good" : null}
                        label={r.atRisk === true ? "At risk" : r.atRisk === false ? "Not at risk" : "—"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
